import React, { FC, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'src/components/widgets/icon';
import { T } from 'src/shared/helpers';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import { IMPORT_TARGET_BACKGROUND, LibraryImportStatus, LibraryImportTasks } from './libraryImportTasks';

// Fixed overlay that dims the whole image-gallery page — its header, the categories sidebar and the content — but NOT
// the app's main navigation menu: it starts at the nav width (`--app-sidebar-width`) and reaches the right edge.
// Fixed positioning keeps both the dim and the centered dialog put while the page
// scrolls. Kept mounted (display:none when hidden, not unmounted) so the wrapped LibraryImportTasks keeps polling and
// can re-block as soon as a new import starts.
//
// `$contained` switches to absolute positioning filling the nearest positioned ancestor instead of the viewport. Used
// for the Background gallery, which lives inside a fullscreen dialog: a viewport-fixed overlay at this z-index would
// sit *under* the dialog, so it must be contained to the dialog content instead.
const Overlay = styled.div<{ $visible: boolean; $contained?: boolean }>`
    display: ${(props) => (props.$visible ? 'flex' : 'none')};
    position: ${(props) => (props.$contained ? 'absolute' : 'fixed')};
    top: 0;
    bottom: 0;
    left: ${(props) => (props.$contained ? '0' : 'var(--app-sidebar-width, 295px)')};
    right: 0;
    z-index: ${(props) => (props.$contained ? 1 : 30)};
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(0, 0, 0, 0.55);
`;

// Centered by the overlay's flexbox and sized to the viewport, so it stays put and centered relative to the dimmed
// area regardless of scroll.
const DialogBox = styled.div`
    display: flex;
    flex-direction: column;
    width: min(720px, 100%);
    max-height: calc(100vh - 96px);
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
    overflow: hidden;
`;

const DialogHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 24px 8px;
`;

const HeaderTexts = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const Title = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #09090b;
`;

const Subtitle = styled.p`
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: #71717a;
`;

const CloseButton = styled.button`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 4px;
    display: flex;
    flex-shrink: 0;
    color: #71717a;

    &:hover {
        color: #09090b;
    }
`;

const ListScroll = styled.div`
    overflow-y: auto;
`;

interface GalleryImportBlockingOverlayProps {
    // Import target to track (0=ImageGallery, 1=Background). Only tasks of this target block/progress here.
    target: number;
    // Refresh the image grid as images are imported (progressive reveal) and once every task completes.
    onProgress: () => void;
    onCompleted: () => void;
    // Bump to force an immediate refetch (e.g. right after a manual import was triggered elsewhere).
    refreshSignal?: number;
    // Contain the overlay to its positioned ancestor (absolute) instead of the viewport (fixed). Used inside the
    // fullscreen Background gallery dialog.
    contained?: boolean;
}

export const GalleryImportBlockingOverlay: FC<GalleryImportBlockingOverlayProps> = ({ target, onProgress, onCompleted, refreshSignal, contained }) => {
    const [status, setStatus] = useState<LibraryImportStatus>({ hasActive: false, hasError: false });
    const [dismissed, setDismissed] = useState(false);

    const handleStatusChange = useCallback((next: LibraryImportStatus) => setStatus(next), []);

    // A newly started import must always re-block, even if the seller dismissed a previous error dialog.
    useEffect(() => {
        if (status.hasActive) {
            setDismissed(false);
        }
    }, [status.hasActive]);

    // Closable only when nothing is processing anymore (i.e. stuck on an error) so the seller is never trapped; while an
    // import is actually running there is no close button and no action.
    const closable = !status.hasActive;
    const errorOnly = status.hasError && !status.hasActive;
    const visible = (status.hasActive || status.hasError) && !dismissed;

    const isBackground = target === IMPORT_TARGET_BACKGROUND;

    return (
        <Overlay $visible={visible} $contained={contained}>
            <DialogBox role='dialog' aria-modal='true'>
                <DialogHeader>
                    <HeaderTexts>
                        <Title>{isBackground ? T._('Importing the background gallery', 'Admin') : T._('Importing the Free Image Pack', 'Admin')}</Title>
                        <Subtitle>
                            {errorOnly
                                ? T._('Some images could not be imported. You can close this and keep working — the rest of your gallery is ready.', 'Admin')
                                : isBackground
                                  ? T._(
                                        "We're importing the background gallery into your account. The images will appear here as soon as they're ready.",
                                        'Admin'
                                    )
                                  : T._(
                                        "We're importing the Free Image Pack into your account. The images will appear here as soon as they're ready.",
                                        'Admin'
                                    )}
                        </Subtitle>
                    </HeaderTexts>
                    {closable && (
                        <CloseButton type='button' aria-label={T._('Close', 'Admin')} onClick={() => setDismissed(true)}>
                            <Icon>
                                <CloseIcon />
                            </Icon>
                        </CloseButton>
                    )}
                </DialogHeader>
                <ListScroll>
                    <LibraryImportTasks
                        target={target}
                        onStatusChange={handleStatusChange}
                        onTasksProgress={onProgress}
                        onTasksCompleted={onCompleted}
                        refreshSignal={refreshSignal}
                        progressOnly
                    />
                </ListScroll>
            </DialogBox>
        </Overlay>
    );
};

export default GalleryImportBlockingOverlay;
